import { SortConfig } from '@celito.clients/list-view-engine';
import { ReferenceSelector, Stack } from '@celito.clients/shared';
import {
  createTestAttribute,
  getReferencePickerType,
} from '@celito.clients/utils';
import { ReferenceSelectorItem } from 'libs/shared/src/lib/reference-selector/src/reference-selector.model';
import { RulesComponentListViewFilter } from 'libs/shared/src/lib/rules-component/types/rules-component.types';
import {
  Controller,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
} from 'react-hook-form';

import { getReferenceSortConfig } from '../../../utils/attribute-config';
import { ControlledReferenceSelectorProps } from './controlled-reference-selector.model';
import classes from './controlled-reference-selector.module.css';

interface ControlledReferenceSelectorViewProps<T extends FieldValues>
  extends ControlledReferenceSelectorProps<T> {
  getReferencePickerFiltersByObjectName: (
    objectName: string,
    recordDetails: Record<string, any> | undefined,
    excludeSelfAttachedId: string | undefined
  ) => RulesComponentListViewFilter;
}

export const ControlledReferenceSelectorView = <T extends FieldValues>(
  props: ControlledReferenceSelectorViewProps<T>
) => {
  const { name } = props.attribute;
  const { setValue } = useFormContext<T>();
  const roles = (props.attribute?.roles || []).map((role: string) => {
    return { name: role };
  });

  const sortConfig: SortConfig = getReferenceSortConfig(props.attribute);

  return (
    <Controller
      name={name as Path<T>}
      control={props.control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const objectName = props?.attribute?.relationship?.objectName || '';

        const handleOnRowReorder = (
          draggedIndex: number,
          targetRowIndex: number
        ) => {
          const data = value?.slice() ?? [];
          data?.splice(targetRowIndex, 0, data.splice(draggedIndex, 1)[0]);
          onChange([...data]);
        };

        return (
          <Stack
            data-testid={`reference-selector-container-${createTestAttribute(
              props.attribute?.label
            )}`}
            className={classes.section}
          >
            <ReferenceSelector
              objectName={objectName}
              label={props?.attribute?.label}
              referencePickerProps={{
                required: props.attribute?.isRequired,
                onOptionSelect: (_, data) => null,
                referencePicker: {
                  referenceUrl: props?.attribute?.referenceUrl as string,
                  defaultReferenceFilter:
                    props.attribute.defaultReferenceFilter,
                  filters: props.getReferencePickerFiltersByObjectName(
                    objectName,
                    props?.recordDetails,
                    props?.recordDetails?.name
                  ),
                  sortConfig,
                  pickerType: getReferencePickerType(objectName),
                  peoplePickerRoles: roles,
                },
                helperLabelText: props.attribute?.helpText,
                multiselect: true,
                errorMessage: error?.message,
                disabled: !props.attribute.isEditable,
                completeAttributeConfig: props.completeAttributeConfig,
              }}
              onSelectorChange={(data, isDirty = true) => {
                if (isDirty) {
                  onChange(data);
                } else {
                  setValue?.(name as Path<T>, data as PathValue<T, Path<T>>, {
                    shouldDirty: isDirty,
                  });
                }
              }}
              onRemoveItem={(item) => {
                onChange(
                  value.filter(
                    (selectedItem: ReferenceSelectorItem) =>
                      selectedItem.name !== item.name
                  )
                );
              }}
              selectedItems={props.watch ? props.watch(name as Path<T>) : []}
              onRowReorder={handleOnRowReorder}
              getLatestVersionOnly={props.attribute.getLatestVersionOnly}
              mode={props.mode}
              excludeSelfAttachedId={
                props.attribute.getAllVersions
                  ? props?.recordDetails?.uuid
                  : props?.recordDetails?.name
              }
              getAllVersions={props.attribute.getAllVersions}
            />
          </Stack>
        );
      }}
    />
  );
};
